import type { SegmentPartnerRevenueMethod } from '@openx/types/gqlTypes';
import type { AudiencePricing } from '@openx/types/targeting/audience';
import type { ContextualPricingApi } from '@openx/types/targeting/contextualPricing';
import { useMemo } from 'react';
import { useDealContext } from '../../utils/context/DealContext';
import { CONTEXTUAL_PLATFORM_FEE_SUPPORTED } from './constants';
import { calculateFeeSummary } from './utils/feeSummary';
import {
  buildPartnerFeeRows,
  getUniquePartnerIds,
  hasBothPartnerTypes,
  hasPlatformFee,
  type PartnerFeeRow,
} from './utils/partnerFeeRows';

type UsePartnerFeesParams = Partial<AudiencePricing> & {
  contextualPricing?: ContextualPricingApi | null | undefined;
  segmentPartnerRevenueMethod: SegmentPartnerRevenueMethod | null | undefined;
};

/** Partner fee rows of both partner types, their organizations and the fee summary, for the pricing details views. */
export const usePartnerFees = ({
  pricing_by_org,
  totals,
  contextualPricing,
  segmentPartnerRevenueMethod,
}: UsePartnerFeesParams) => {
  const hasAudienceData = !!pricing_by_org && !!totals;
  const rows = useMemo(
    () => buildPartnerFeeRows(hasAudienceData ? { pricing_by_org } : null, contextualPricing),
    [hasAudienceData, pricing_by_org, contextualPricing]
  );
  // one platform fee entry per partner (shared by its rows), only for partners with a platform-fee row
  const partnerIds = useMemo(
    () => getUniquePartnerIds(rows.filter(row => hasPlatformFee(row, CONTEXTUAL_PLATFORM_FEE_SUPPORTED))),
    [rows]
  );
  // every listed partner is named, whether or not it carries a platform fee
  const accountIds = useMemo(
    () =>
      getUniquePartnerIds(rows)
        .map(Number)
        .filter(id => !Number.isNaN(id)),
    [rows]
  );
  const { useOrganizationsByIdsFetch } = useDealContext();
  const { organizations, isLoading } = useOrganizationsByIdsFetch(accountIds);

  const feeSummary = useMemo(
    () =>
      segmentPartnerRevenueMethod
        ? calculateFeeSummary({
            segmentPartnerRevenueMethod,
            audienceTotals: hasAudienceData ? totals : null,
            contextualPricing,
          })
        : null,
    [segmentPartnerRevenueMethod, hasAudienceData, totals, contextualPricing]
  );

  return {
    rows,
    partnerIds,
    hasPlatformFee: (row: Pick<PartnerFeeRow, 'partnerType'>) => hasPlatformFee(row, CONTEXTUAL_PLATFORM_FEE_SUPPORTED),
    shouldShowPartnerType: hasBothPartnerTypes(rows),
    hasFees: hasAudienceData || rows.length > 0,
    feeSummary,
    organizations,
    isLoading,
  };
};
